import {
  ChevronDownIcon,
  COCMyTeamIcon,
  COCPublicIcon,
  COCSpecificTeamIcon,
} from '@/assets/icons/icons';
import { Dropdown, Flex, message } from 'antd';
import React, { useState, useEffect } from 'react';
import AudienceOption from './AudienceOptions';
import AudienceTeamMenu from './AudienceTeamMenu';
import { AllCommonTeams, ShipmentTeam } from '../ReplyCommentBox';
import { useSpecificTeams } from './SpecificTeamsContext';

export type AudienceType = 'my_team' | 'specific_teams' | 'public';

export type Team = ShipmentTeam;

export interface AudienceMeta {
  audience: AudienceType;
  teamIds?: string[];
}

export interface AudienceSelectorProps {
  myTeams: Team[];
  specificTeams: AllCommonTeams[];
  primaryTeamName?: string;
  defaultAudience?: string;
  hideIfSingleTeam?: boolean;
  onChange?: (meta: AudienceMeta) => void;
  setSelectedTeamID?: React.Dispatch<React.SetStateAction<string[]>>;
  custody?: {
    audience_type?: string;
    audience_teams?: { id: string }[];
  };
}

export default function AudienceSelector({
  myTeams,
  specificTeams,
  primaryTeamName,
  defaultAudience = 'my_team',
  hideIfSingleTeam = false,
  onChange,
  setSelectedTeamID,
  custody,
}: AudienceSelectorProps) {
  const context = useSpecificTeams();
  const enableLoadMoreTeams = context.enableLoadMoreTeams ?? false;
  const hasMoreTeams = context.hasMoreTeams ?? false;
  const loadingTeams = context.loadingTeams ?? false;
  const onFetchSpecificTeams = context.onFetchSpecificTeams;

  const finalSpecificTeams = enableLoadMoreTeams
    ? context.specificTeams || []
    : specificTeams;

  const [open, setOpen] = useState(false);
  const [menuLevel, setMenuLevel] = useState<'main' | 'team'>('main');
  const [currentMenuType, setCurrentMenuType] = useState<
    'my_team' | 'specific_teams' | null
  >(null);

  const [selectedAudience, setSelectedAudience] =
    useState<string>(defaultAudience);

  const [selectedMyTeamIds, setSelectedMyTeamIds] = useState<string[]>(
    custody?.audience_type === 'my_team'
      ? custody?.audience_teams?.length
        ? custody.audience_teams.map((t) => t.id)
        : myTeams?.map((t) => t.id)
      : myTeams?.map((t) => t.id)
  );

  const [selectedSpecificTeamIds, setSelectedSpecificTeamIds] = useState<
    string[]
  >(
    custody?.audience_type === 'specific_teams'
      ? custody?.audience_teams?.length
        ? custody.audience_teams.map((t) => t.id)
        : finalSpecificTeams?.map((t) => t.id)
      : finalSpecificTeams?.map((t) => t.id)
  );

  const [search, setSearch] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Specific Teams API Pagination state
  const [teamsPage, setTeamsPage] = useState<number>(1);

  // Synchronize internal states with props when custody changes (e.g. node switches or panel reopens)
  useEffect(() => {
    if (custody?.audience_type) {
      setSelectedAudience(custody.audience_type);
      const teamIds = custody.audience_teams?.map((t) => t.id) || [];

      if (custody.audience_type === 'my_team') {
        const isSame =
          selectedMyTeamIds.length === teamIds.length &&
          selectedMyTeamIds.every((v, i) => v === teamIds[i]);
        if (!isSame) {
          setSelectedMyTeamIds(teamIds);
        }
      } else if (custody.audience_type === 'specific_teams') {
        const isSame =
          selectedSpecificTeamIds.length === teamIds.length &&
          selectedSpecificTeamIds.every((v, i) => v === teamIds[i]);
        if (!isSame) {
          setSelectedSpecificTeamIds(teamIds);
        }
      }
    } else {
      setSelectedAudience(defaultAudience);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custody, defaultAudience]);

  // Debounced load/search of specific teams using the passed fetching function
  useEffect(() => {
    if (
      open &&
      menuLevel === 'team' &&
      currentMenuType === 'specific_teams' &&
      enableLoadMoreTeams
    ) {
      const delayDebounceFn = setTimeout(() => {
        onFetchSpecificTeams?.(1, search, false);
        setTeamsPage(1);
      }, 300);

      return () => clearTimeout(delayDebounceFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, menuLevel, currentMenuType, open, enableLoadMoreTeams]);

  // Reset page when search or tab changes
  useEffect(() => {
    setTeamsPage(1);
  }, [search, currentMenuType]);

  const handleLoadMoreTeams = () => {
    if (enableLoadMoreTeams) {
      const nextPage = teamsPage + 1;
      onFetchSpecificTeams?.(nextPage, search, true);
      setTeamsPage(nextPage);
    }
  };

  const handleSearchChange = (v: string) => setSearch(v);
  const handleSearchClear = () => setSearch('');

  const closeMenu = () => {
    setOpen(false);
    setMenuLevel('main');
    setCurrentMenuType(null);
    setValidationError(null);
  };

  // ======================
  // HANDLERS
  // ======================
  const selectMyTeam = () => {
    if (myTeams?.length > 1) {
      setMenuLevel('team');
      setCurrentMenuType('my_team');
    } else {
      setSelectedAudience('my_team');
      closeMenu();
      onChange?.({ audience: 'my_team', teamIds: [myTeams[0]?.id] });
    }
  };

  const selectSpecificTeams = () => {
    setMenuLevel('team');
    setCurrentMenuType('specific_teams');
  };

  const selectPublic = () => {
    setSelectedAudience('public');
    closeMenu();
    onChange?.({ audience: 'public' });
  };

  const handleApply = (selectedIds: string[]) => {
    if (selectedIds.length === 0) {
      setValidationError('Select at least one team');
      return;
    }

    if (currentMenuType === 'my_team') {
      setSelectedMyTeamIds(selectedIds);
    } else if (currentMenuType === 'specific_teams') {
      setSelectedSpecificTeamIds(selectedIds);
    }

    setSelectedAudience(currentMenuType!);
    setSelectedTeamID?.(selectedIds);
    onChange?.({ audience: currentMenuType!, teamIds: selectedIds });

    closeMenu();
  };

  const handleReset = () => {
    setSearch('');
    if (currentMenuType === 'my_team') {
      const ids = myTeams?.map((t) => t.id);
      setSelectedMyTeamIds(ids);
      setSelectedTeamID?.(ids);
    } else {
      const ids = finalSpecificTeams?.map((t) => t.id) || [];
      setSelectedSpecificTeamIds(ids);
      setSelectedTeamID?.(ids);
    }
    message.info('Selection reset to all teams');
    closeMenu();
  };

  // ======================
  // UI
  // ======================
  const renderButtonLabel = () => {
    let Icon: React.FC<{ size: number }>;
    let label: string;

    switch (selectedAudience) {
      case 'my_team':
        Icon = COCMyTeamIcon;
        label = `My team${primaryTeamName ? ` (${primaryTeamName})` : ''}`;
        break;
      case 'specific_teams':
        Icon = COCSpecificTeamIcon;
        label = `Specific teams (${selectedSpecificTeamIds.length})`;
        break;
      default:
        Icon = COCPublicIcon;
        label = 'Public';
    }

    return (
      <Flex gap={4} align='center' className='zl-para-12 color-black'>
        <Icon size={16} />
        <span className='audience-hidden'>{label}</span>
      </Flex>
    );
  };

  const mainMenu = (
    <div className='COC-audience-dropdown'>
      <div className='zl-heading-12 gray-5e ml-4'>
        Choose who sees this note
      </div>

      <AudienceOption
        icon={<COCMyTeamIcon />}
        title={`My team${myTeams?.length > 1 ? 's' : ''}`}
        description={
          myTeams?.length > 1
            ? 'Only visible to selected teams'
            : `Only members of ${primaryTeamName ?? 'your team'} can see`
        }
        onClick={selectMyTeam}
        showArrow={myTeams?.length > 1}
      />

      <AudienceOption
        icon={<COCSpecificTeamIcon />}
        title={`Specific teams (${selectedSpecificTeamIds.length})`}
        description='Only seen by specific users'
        onClick={selectSpecificTeams}
        showArrow
      />

      <AudienceOption
        icon={<COCPublicIcon />}
        title='Public'
        description='Can be seen by all'
        onClick={selectPublic}
      />
    </div>
  );

  const currentSelectedIds =
    currentMenuType === 'my_team' ? selectedMyTeamIds : selectedSpecificTeamIds;

  const teamMenu = currentMenuType && (
    <AudienceTeamMenu
      type={currentMenuType}
      teams={currentMenuType === 'my_team' ? myTeams : finalSpecificTeams}
      selectedTeamIds={currentSelectedIds}
      search={search}
      validationError={validationError}
      onBack={() => setMenuLevel('main')}
      onSearchChange={handleSearchChange}
      onSearchClear={handleSearchClear}
      onSelectionChange={
        currentMenuType === 'my_team'
          ? setSelectedMyTeamIds
          : setSelectedSpecificTeamIds
      }
      onApply={handleApply}
      onReset={handleReset}
      hasMore={
        currentMenuType === 'specific_teams' && enableLoadMoreTeams
          ? hasMoreTeams
          : false
      }
      loadingMore={
        currentMenuType === 'specific_teams' && enableLoadMoreTeams
          ? loadingTeams
          : false
      }
      onLoadMore={handleLoadMoreTeams}
    />
  );

  if (
    hideIfSingleTeam &&
    (myTeams?.length <= 1 || finalSpecificTeams?.length <= 1)
  )
    return null;

  return (
    <Flex align='center'>
      <Dropdown
        className='audience-selector-dropdown'
        open={open}
        onOpenChange={setOpen}
        dropdownRender={() => (menuLevel === 'main' ? mainMenu : teamMenu)}
        // placement='bottom'
        trigger={['click']}
        getPopupContainer={() => document.body}
        autoAdjustOverflow
        overlayStyle={{ zIndex: 2000 }}
      >
        <Flex gap={4} align='center' className='cursor-pointer'>
          {renderButtonLabel()} <ChevronDownIcon size={16} />
        </Flex>
      </Dropdown>
    </Flex>
  );
}
